/**
 * "Setup is unfinished" on the settings landing page (#2497).
 *
 * Both the wizard summary and the global reminder point here, but this page
 * said nothing — a workshop that followed the advice arrived at a grid of
 * sixteen settings with no indication of which one it came for.
 *
 * This also gives `resume()` the caller its own comment promised since the
 * wizard was written ("used by the settings entry point"): reopening from
 * here clears the 24 h snooze, because a workshop that navigated to settings
 * on purpose is not postponing anything.
 *
 * Reads the same readiness rules as the summary and the reminder. Three
 * places reporting the same gaps must not be able to disagree.
 */

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { WorkshopSettings } from '@/types';
import { useOnboarding } from '@/features/onboarding/useOnboarding';
import { useWorkshopReadiness } from './useWorkshopReadiness';
import { byWorkflowOrder } from './readinessOrder';
import type { ReadinessIssue } from './workshopReadiness';
import type { SettingsTab } from './settingsTabs';

interface Props {
  settings: WorkshopSettings;
  /** Opens a tab on this page rather than reloading the route. */
  onOpen: (tab: SettingsTab) => void;
}

export function SettingsOnboardingCard({ settings, onOpen }: Props) {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { isComplete, canManage, resume } = useOnboarding();
  const { issues, isLoading } = useWorkshopReadiness(settings);

  // Nothing to say, or not this user's job to hear it.
  if (!canManage || isLoading || issues.length === 0) return null;

  const sorted = [...issues].sort(byWorkflowOrder);

  const openIssue = (issue: ReadinessIssue) => {
    onOpen(issue.target.tab as SettingsTab);
  };

  const continueWizard = () => {
    // Clearing the snooze is the point: arriving here is the opposite of
    // "not now".
    void resume();
    void navigate('/onboarding');
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }} data-testid="settings-onboarding-card">
      <CardContent>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {isComplete ? t('onboardingCard.gapsTitle') : t('onboardingCard.unfinishedTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {isComplete ? t('onboardingCard.gapsBody') : t('onboardingCard.unfinishedBody')}
        </Typography>

        <List dense disablePadding>
          {sorted.map((issue) => (
            <ListItemButton
              key={issue.key}
              onClick={() => openIssue(issue)}
              data-testid={`onboarding-gap-${issue.key}`}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {issue.severity === 'warning' ? (
                  <WarningAmberIcon color="warning" fontSize="small" />
                ) : (
                  <ErrorOutlineIcon color="error" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText primary={t(`readiness.issue.${issue.key}`)} />
              <ChevronRightIcon fontSize="small" color="action" />
            </ListItemButton>
          ))}
        </List>

        {/*
          Only offered while the wizard has not been finished. Once it has,
          sending someone back through it is the wrong instruction — the gaps
          above are the shorter route, and several sit on tabs it never covered.
        */}
        {!isComplete && (
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" size="small" onClick={continueWizard}>
              {t('onboardingCard.continueWizard')}
            </Button>
          </Box>
        )}

        {isComplete && sorted.every((i) => i.severity === 'warning') && (
          <Alert severity="info" sx={{ mt: 2 }} variant="outlined">
            {t('onboardingCard.warningsOnly')}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
